import { downloadZip } from 'client-zip';
import moment from 'moment-timezone';
import streamSaver from 'streamsaver';

import SessionService from '../../../services/sessions/sessions.service';

// Defaults used by studio-api when building the partner zip name.
// Mirrors helpers/fileManagementHelper.js -> fileDLName(session, true).
const PARTNER_ZIP_TIMEZONE = 'Europe/Lisbon';
const PARTNER_ZIP_DEFAULT_CLIENT_NAME = 'music creator';

export interface DownloadProgressInfo {
  percentage: number;
  bytesWritten: number;
  totalSize: number;
}

interface CreateZipOptions {
  onProgress?: (progress: DownloadProgressInfo) => void;
  onComplete?: () => void;
}

// A file in the session, as needed to build a zip entry.
export interface ZipFile {
  filePath: string;
  filename: string;
  sizeBytes: number;
}

// escape characters not allowed as a filename on Windows & macOS
const escapeInvalidChars = (fileName: string): string => fileName.replace(/[<>:"/\\|?*]/g, '_');

// For the given list of file URLs, generate a zip and stream it to the user's
// disk. Streaming avoids loading every file into memory at once, so this scales
// to large deliverables.
const streamMultipleFilesToZip = async (
  fileUrls: { url: string; filename: string; size: number }[],
  zipFileName: string,
  options?: CreateZipOptions
): Promise<void> => {
  // Generator: yields each file's fetch stream one by one.
  async function* getFileStreams() {
    for (const { url, filename, size } of fileUrls) {
      const res = await fetch(url);
      // NOTE: any failed fetch aborts the whole zip.
      if (!res.ok || !res.body) {
        throw new Error(`Failed to fetch ${url}`);
      }
      yield {
        name: filename,
        size,
        input: res.body
      };
    }
  }

  // Metadata lets client-zip predict the Content-Length up front, which we use
  // for the download size hint and progress percentage.
  const metadata = fileUrls.map(({ filename, size }) => ({ name: filename, size }));

  const zipResponse = downloadZip(getFileStreams(), { metadata });
  const zipStream = zipResponse.body;
  if (!zipStream) {
    throw new Error('Failed to create ZIP stream');
  }

  const contentLengthHeader = zipResponse.headers.get('Content-Length');
  const totalSize = parseInt(contentLengthHeader || '0', 10);

  const fileStream = streamSaver.createWriteStream(zipFileName, { size: totalSize });

  if (!options?.onProgress || totalSize === 0) {
    await zipStream.pipeTo(fileStream);
  } else {
    let bytesWritten = 0;
    const progressStream = new TransformStream({
      transform(chunk, controller) {
        bytesWritten += chunk.byteLength;
        const percentage = totalSize > 0 ? (bytesWritten / totalSize) * 100 : 0;
        options?.onProgress?.({ percentage, bytesWritten, totalSize });
        controller.enqueue(chunk);
      }
    });
    await zipStream.pipeThrough(progressStream).pipeTo(fileStream);
  }

  options?.onComplete?.();
};

interface UseClientZip {
  createZip: (files: ZipFile[], zipFilename: string, options?: CreateZipOptions) => Promise<void>;
  generateZipFilename: (parts: { trackName?: string; folderLabel?: string }) => string;
}

export const useClientZip = (sessionId: number): UseClientZip => {
  // Builds a zip in the browser from the given files and streams it to disk.
  const createZip = async (files: ZipFile[], zipFilename: string, options?: CreateZipOptions): Promise<void> => {
    // Resolve a presigned download URL for each file.
    const bundles = await Promise.all(
      files.map(async file => {
        const { downloadUrl } = (await SessionService.getDownloadLink(sessionId, file.filePath)).data;
        return {
          url: downloadUrl,
          filename: file.filename,
          size: file.sizeBytes
        };
      })
    );

    await streamMultipleFilesToZip(bundles, zipFilename, options);
  };

  const generateZipFilename = (parts: {
    trackName?: string;
    folderLabel?: string;
    // Partner downloads reproduce the deprecated studio-api zip name.
    isPartner?: boolean;
    customerName?: string;
    serviceType?: string;
    sessionDate?: string;
  }): string => {
    if (parts.isPartner) {
      // Matches studio-api fileDLName(session, true):
      // `${date}-${sessionId}-${clientName}-${trackTitle}-${serviceType}.zip`
      const date = parts.sessionDate
        ? moment(parts.sessionDate).tz(PARTNER_ZIP_TIMEZONE).format('YYYY-MM-DD')
        : '';
      const clientName = parts.customerName || PARTNER_ZIP_DEFAULT_CLIENT_NAME;
      const partnerSegments = [date, String(sessionId), clientName, parts.trackName, parts.serviceType]
        .filter(Boolean)
        .map(segment => escapeInvalidChars(segment as string));
      return `${partnerSegments.join('-')}.zip`;
    }

    const segments = [String(sessionId), parts.trackName, parts.folderLabel]
      .filter(Boolean)
      .map(segment => escapeInvalidChars(segment as string));
    return `${segments.join('-')}.zip`;
  };

  return { createZip, generateZipFilename };
};
